import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Plus, Search, Download, Upload } from 'lucide-react';
import Sidebar, { useSidebar } from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import TopNavbar from '../../components/ui/TopNavbar';

const GeneralLedger = () => {
  const { isCollapsed } = useSidebar();
  const [expandedCategories, setExpandedCategories] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAccountType, setSelectedAccountType] = useState('all');
  const [showAddEntryModal, setShowAddEntryModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Mock Chart of Accounts data
  const chartOfAccounts = [
    {
      id: 1,
      code: '1000',
      name: 'Assets',
      type: 'Asset',
      subAccounts: [
        { id: 101, code: '1100', name: 'Current Assets', balance: 850000, type: 'Asset' },
        { id: 102, code: '1110', name: 'Cash and Cash Equivalents', balance: 250000, type: 'Asset' },
        { id: 103, code: '1120', name: 'Accounts Receivable', balance: 400000, type: 'Asset' },
        { id: 104, code: '1130', name: 'Inventory', balance: 200000, type: 'Asset' },
        { id: 105, code: '1200', name: 'Fixed Assets', balance: 1500000, type: 'Asset' },
      ]
    },
    {
      id: 2,
      code: '2000',
      name: 'Liabilities',
      type: 'Liability',
      subAccounts: [
        { id: 201, code: '2100', name: 'Current Liabilities', balance: 320000, type: 'Liability' },
        { id: 202, code: '2110', name: 'Accounts Payable', balance: 180000, type: 'Liability' },
        { id: 203, code: '2120', name: 'Accrued Expenses', balance: 140000, type: 'Liability' },
        { id: 204, code: '2200', name: 'Long-term Debt', balance: 750000, type: 'Liability' },
      ]
    },
    {
      id: 3,
      code: '3000',
      name: 'Equity',
      type: 'Equity',
      subAccounts: [
        { id: 301, code: '3100', name: 'Owner Equity', balance: 1000000, type: 'Equity' },
        { id: 302, code: '3200', name: 'Retained Earnings', balance: 280000, type: 'Equity' },
      ]
    },
    {
      id: 4,
      code: '4000',
      name: 'Revenue',
      type: 'Revenue',
      subAccounts: [
        { id: 401, code: '4100', name: 'Sales Revenue', balance: 2500000, type: 'Revenue' },
        { id: 402, code: '4200', name: 'Service Revenue', balance: 450000, type: 'Revenue' },
        { id: 403, code: '4300', name: 'Other Income', balance: 75000, type: 'Revenue' },
      ]
    },
    {
      id: 5,
      code: '5000',
      name: 'Expenses',
      type: 'Expense',
      subAccounts: [
        { id: 501, code: '5100', name: 'Cost of Goods Sold', balance: 1200000, type: 'Expense' },
        { id: 502, code: '5200', name: 'Operating Expenses', balance: 650000, type: 'Expense' },
        { id: 503, code: '5210', name: 'Salaries and Wages', balance: 400000, type: 'Expense' },
        { id: 504, code: '5220', name: 'Rent and Utilities', balance: 120000, type: 'Expense' },
        { id: 505, code: '5230', name: 'Marketing', balance: 80000, type: 'Expense' },
        { id: 506, code: '5300', name: 'Depreciation', balance: 50000, type: 'Expense' },
      ]
    },
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev?.[categoryId]
    }));
  };

  const filteredAccounts = chartOfAccounts?.filter(category => {
    if (selectedAccountType !== 'all' && category?.type !== selectedAccountType) {
      return false;
    }
    if (searchTerm) {
      const matchesParent = category?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                          category?.code?.includes(searchTerm);
      const matchesChild = category?.subAccounts?.some(sub => 
        sub?.name?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        sub?.code?.includes(searchTerm)
      );
      return matchesParent || matchesChild;
    }
    return true;
  });

  const handleAddEntry = () => {
    setShowAddEntryModal(true);
  };

  const handleSaveEntry = (entryData) => {
    console.log('Saving ledger entry:', entryData);
    // TODO: Implement actual save with Supabase
    alert('✅ Ledger entry added successfully!');
    setShowAddEntryModal(false);
  };

  const handleEditEntry = (entryId) => {
    console.log('Editing entry:', entryId);
    setShowEditModal(true);
    // TODO: Load entry data
  };

  const handleUpdateEntry = (entryData) => {
    console.log('Updating entry:', entryData);
    // TODO: Implement update
    alert('✅ Ledger entry updated!');
    setShowEditModal(false);
  };

  const handleDeleteEntry = (entryId) => {
    if (confirm('Are you sure you want to delete this entry?')) {
      console.log('Deleting entry:', entryId);
      // TODO: Implement deletion
      alert('✅ Ledger entry deleted!');
    }
  };

  const handleReconcile = (accountId) => {
    console.log('Reconciling account:', accountId);
    // TODO: Implement reconciliation
    alert('✅ Account reconciliation started!');
  };

  const handleExportLedger = () => {
    console.log('Exporting ledger');
    // TODO: Implement export
    alert('✅ Exporting general ledger...');
  };

  const handleGenerateTrialBalance = () => {
    console.log('Generating trial balance');
    // TODO: Implement trial balance generation
    alert('✅ Trial balance generated!');
  };

  return (
    <div className="flex flex-col h-screen bg-background">
      <TopNavbar />
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header - positioned below TopNavbar */}
        <div className="sticky top-0 z-40 bg-white border-b border-gray-200 shadow-sm mt-[65px]">
          <div className="px-4 sm:px-6 lg:px-8 py-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">General Ledger</h1>
                <p className="text-gray-600 mt-1 text-sm sm:text-base">Manage your chart of accounts and financial structure</p>
              </div>
              <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                <Button variant="outline" className="flex items-center gap-2 flex-1 sm:flex-initial justify-center">
                  <Upload className="h-4 w-4" />
                  Import
                </Button>
                <Button variant="outline" className="flex items-center gap-2 flex-1 sm:flex-initial justify-center">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
                <Button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 w-full sm:w-auto justify-center">
                  <Plus className="h-4 w-4" />
                  Add Account
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search accounts by name or code..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={selectedAccountType}
                onChange={(e) => setSelectedAccountType(e?.target?.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Types</option>
                <option value="Asset">Assets</option>
                <option value="Liability">Liabilities</option>
                <option value="Equity">Equity</option>
                <option value="Revenue">Revenue</option>
                <option value="Expense">Expenses</option>
              </select>
            </div>
          </div>
        </div>

        {/* Chart of Accounts */}
        <div className="flex-1 overflow-auto p-4 sm:p-6 lg:px-8">
          <div className="bg-white rounded-lg shadow">
            {/* Table Header */}
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-gray-50 border-b border-gray-200 font-semibold text-gray-700">
              <div className="col-span-5">Account Name</div>
              <div className="col-span-2">Account Code</div>
              <div className="col-span-2">Type</div>
              <div className="col-span-2 text-right">Balance</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>

            {/* Account Categories */}
            {filteredAccounts?.map(category => (
              <div key={category?.id} className="border-b border-gray-200 last:border-b-0">
                {/* Parent Category */}
                <div
                  onClick={() => toggleCategory(category?.id)}
                  className="grid grid-cols-12 gap-4 px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="col-span-5 flex items-center gap-2 font-semibold text-gray-900">
                    {expandedCategories?.[category?.id] ? (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-500" />
                    )}
                    {category?.name}
                  </div>
                  <div className="col-span-2 text-gray-600">{category?.code}</div>
                  <div className="col-span-2">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      category?.type === 'Asset' ? 'bg-green-100 text-green-800' :
                      category?.type === 'Liability' ? 'bg-red-100 text-red-800' :
                      category?.type === 'Equity' ? 'bg-blue-100 text-blue-800' :
                      category?.type === 'Revenue'? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {category?.type}
                    </span>
                  </div>
                  <div className="col-span-2"></div>
                  <div className="col-span-1"></div>
                </div>

                {/* Sub Accounts */}
                {expandedCategories?.[category?.id] && category?.subAccounts && (
                  <div className="bg-gray-50">
                    {category?.subAccounts?.map(subAccount => (
                      <div
                        key={subAccount?.id}
                        className="grid grid-cols-12 gap-4 px-6 py-3 hover:bg-gray-100 transition-colors"
                      >
                        <div className="col-span-5 pl-8 text-gray-700">{subAccount?.name}</div>
                        <div className="col-span-2 text-gray-600">{subAccount?.code}</div>
                        <div className="col-span-2">
                          <span className={`px-2 py-1 rounded text-xs ${
                            subAccount?.type === 'Asset' ? 'bg-green-50 text-green-700' :
                            subAccount?.type === 'Liability' ? 'bg-red-50 text-red-700' :
                            subAccount?.type === 'Equity' ? 'bg-blue-50 text-blue-700' :
                            subAccount?.type === 'Revenue'? 'bg-purple-50 text-purple-700' : 'bg-orange-50 text-orange-700'
                          }`}>
                            {subAccount?.type}
                          </span>
                        </div>
                        <div className="col-span-2 text-right font-medium text-gray-900">
                          ${subAccount?.balance?.toLocaleString()}
                        </div>
                        <div className="col-span-1 text-right">
                          <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                            Edit
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralLedger;